<script setup>
import { ref, watch, getCurrentInstance, computed } from 'vue'
import { getDatedFilename, download } from '@/main'
import { defaultVTooltipProps } from '@/utils/defaultProps'
import { showWarningToast, showErrorToastWithTimeout } from '@/utils/toast'

const props = defineProps({
  selectedStructures: { type: Array, default: null },
  readyReport: { type: Object, default: null },
  getReport: { type: Function, default: null },
  exportUrl: { type: String, required: true },
  title: { type: String, default: 'Export to file' },
  ignoreFormats: { type: Array, default: () => [] },
  showImages: { type: Boolean, default: true },
  iconName: { type: String, default: 'mdi-download-circle-outline' },
  fileName: { type: String, default: 'cart' },
  count: { type: Number, default: 0 },
  loading: { type: Boolean, default: false },
  showLabel: { type: Boolean, default: false },
  activatorBtnProps: { type: Object, default: () => ({}) },
})

const emit = defineEmits(['prepare-export', 'input'])

const label = computed(() => {
  if (props.showLabel) {
    if (props.count) return `${props.title} (${props.count})`
    return props.title
  }
  return props.count ? `(${props.count})` : ''
})

const { proxy } = getCurrentInstance()
const axios = proxy.$axios

const downloading = ref(false)

const titleWithCount = computed(() =>
  !!props.count ? `${props.title} (count of rows for export: ${props.count})` : props.title,
)

const onExport = async (format) => {
  const dataToExport = props.getReport ? props.getReport() : props.readyReport
  downloading.value = true

  if (props.loading) {
    showWarningToast('The exported file may contain missing values.', {
      title: 'Export started while loading',
      autoHideDelay: 3000,
    })
  }

  return axios
    .post(
      `${props.exportUrl}`,
      {
        chemicals: props.selectedStructures,
        readyReport: dataToExport,
        format: format,
        showImages: props.showImages,
      },
      { responseType: 'blob' },
    )
    .then((response) => {
      const filename = getDatedFilename(props.fileName, format.toLowerCase())
      download(filename, response.data, format.toLowerCase())
    })
    .catch((err) => {
      console.error('Error during export:', err)
      showErrorToastWithTimeout(err, 'Error during export')
    })
    .finally(() => (downloading.value = false))
}

const displayItem = (format) => !props.ignoreFormats.some((ignoreFormat) => ignoreFormat === format)

const btnProps = computed(() => ({
  ...props.activatorBtnProps,
  'disabled':
    downloading.value ||
    (props.selectedStructures !== null && props.selectedStructures.length === 0 && !props.readyReport),
  ...(props.loading
    ? { color: 'warning', title: 'Warning! Data is still loading — the exported file may contain missing values' }
    : {}),
  'aria-label': 'Export selected chemicals to file',
}))

watch(downloading, (newVal) => {
  emit('input', newVal)
})
</script>

<template>
  <v-menu
    offset-y
    transition="scale-transition"
  >
    <template #activator="{ attrs: menuAttrs, on: menuOn }">
      <v-tooltip
        v-bind="defaultVTooltipProps"
        open-on-hover
        top
      >
        <template #activator="{ attrs: tooltipAttrs, on: tooltipOn }">
          <v-btn
            v-bind="{ ...menuAttrs, ...tooltipAttrs, ...btnProps }"
            v-on="{
              ...menuOn,
              ...tooltipOn,
            }"
            class="border flat-button"
          >
            <v-icon :class="{ 'mdi-spin': downloading, 'mr-1': showLabel }">{{ iconName }}</v-icon>
            <span v-if="!!label">{{ label }}</span>
          </v-btn>
        </template>
        <span>
          {{ titleWithCount }}
        </span>
      </v-tooltip>
    </template>

    <v-list>
      <v-list-item
        v-if="displayItem('SDF')"
        @click="onExport('SDF')"
      >
        <v-list-item-icon>
          <v-icon>mdi-file-download-outline</v-icon>
        </v-list-item-icon>
        <v-list-item-title>Export to SDF</v-list-item-title>
      </v-list-item>
      <v-list-item
        v-if="displayItem('CSV')"
        @click="onExport('CSV')"
      >
        <v-list-item-icon>
          <v-icon>mdi-file-delimited-outline</v-icon>
        </v-list-item-icon>
        <v-list-item-title>Export to CSV</v-list-item-title>
      </v-list-item>
      <v-list-item
        v-if="displayItem('XLSX')"
        @click="onExport('XLSX')"
      >
        <v-list-item-icon>
          <v-icon>mdi-file-excel-outline</v-icon>
        </v-list-item-icon>
        <v-list-item-title>Export to XLSX</v-list-item-title>
      </v-list-item>
      <v-list-item
        v-if="displayItem('SMI')"
        @click="onExport('SMI')"
      >
        <v-list-item-icon>
          <v-icon>mdi-file-document-outline</v-icon>
        </v-list-item-icon>
        <v-list-item-title>Export to SMI</v-list-item-title>
      </v-list-item>
    </v-list>
  </v-menu>
</template>
